import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

const UNSAVED_MESSAGE = 'You have unsaved changes here. Leave without saving?';

const NavGuardContext = createContext({ setDirty: () => {}, confirmNavigation: () => true });

/**
 * Lets any navigation trigger (side nav, a register tab switch, the project picker, browser
 * close/refresh) check whether the page currently mid-edit an inline-editable grid before leaving
 * it — one context rather than five copies of this, since a register tab's dirty state has to be
 * checked from places outside that tab (App.js's nav, ProjectRegisters.js's tab bar/project picker).
 *
 * A ref, not state: nothing here needs to re-render on a dirty flag flipping, only to be read at the
 * moment someone tries to navigate.
 */
export function NavGuardProvider({ children }) {
  const dirtyRef = useRef(false);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setDirty = useCallback((value) => { dirtyRef.current = value; }, []);
  const confirmNavigation = useCallback(
    () => !dirtyRef.current || window.confirm(UNSAVED_MESSAGE),
    [],
  );

  const value = useMemo(() => ({ setDirty, confirmNavigation }), [setDirty, confirmNavigation]);
  return <NavGuardContext.Provider value={value}>{children}</NavGuardContext.Provider>;
}

/** An editable grid calls this with its own dirty flag (e.g. `dirtyIds.size > 0`). */
export function useRegisterDirty(isDirty) {
  const { setDirty } = useContext(NavGuardContext);
  useEffect(() => {
    setDirty(isDirty);
    return () => setDirty(false);
  }, [isDirty, setDirty]);
}

/** A nav trigger calls this before acting; a `false` result means the user cancelled. */
export function useNavGuardConfirm() {
  return useContext(NavGuardContext).confirmNavigation;
}
